import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {
  AlertIcon,
  ArrowBothIcon,
  ColumnsIcon,
  ListUnorderedIcon,
  NoteIcon,
  ProjectIcon,
  RowsIcon,
} from '@primer/octicons-react'
import {AriaAlert, Label, Spinner} from '@primer/react'
import {useMemo, useRef} from 'react'

import type {ViewConfiguration} from '../../contexts/views/view-configuration'
import type {Layout} from '../../utils/saved-view-url-params'
import {getFieldsForCapability} from './get-fields-for-capability'
import {Capability, type FieldDefinition, type FieldsData} from './fields'
import type {FieldOption} from '../view-options/field-option'
import {ViewOptionsMenu} from '../view-options/ViewOptionsMenu'
import {getFieldIcon} from './field-icons'
import styles from './SavedViewOptionsMenu.module.css'

const LAYOUT_OPTIONS = [
  {value: 'list', label: 'List', icon: ListUnorderedIcon},
  {value: 'board', label: 'Board', icon: ProjectIcon},
] satisfies ReadonlyArray<{value: Layout; label: string; icon: typeof ListUnorderedIcon}>

function toFieldOption({identifier, label, icon}: FieldDefinition): FieldOption {
  return {identifier, label, icon: getFieldIcon(icon)}
}

/** Sort's leading visual, matching memex: a two-way arrow rotated to point up/down. */
function SortByIcon({size}: {size?: number}) {
  return <ArrowBothIcon size={size} className={styles.sortIcon} />
}

type ViewConfigurationDirtyState = {[Key in keyof ViewConfiguration]: boolean}

interface SavedViewOptionsMenuProps {
  fields: FieldsData | undefined
  /** True while the field definitions are being fetched asynchronously. */
  isPending?: boolean
  /** True when the async fetch for the field definitions failed. */
  isError?: boolean
  configuration: ViewConfiguration
  showDirtyIndicator?: boolean
  /** Applied settings that differ from the saved baseline. */
  configurationDirty?: ViewConfigurationDirtyState
  /** Handler that is called when Discard is picked. The button shows once `showDirtyIndicator` is set. */
  onDiscard?: () => void
  /** Prevents configuration changes while the current configuration is being saved. */
  disabled?: boolean
  /** Handler that is called with the settings each pick changes, including the full visible-field selection. */
  onApply: (changes: Partial<ViewConfiguration>) => void
  /**
   * Handler that is called with the changes a layout, column, swimlane, or sort option would make,
   * once a pick of it looks likely. Returns whether it sent a request. At most
   * `MAX_INTENT_REQUESTS` calls that send one are made each time the menu opens.
   */
  onIntent?: (changes: Partial<ViewConfiguration>) => boolean
}

/** Bounds the speculative requests one open menu sends, whatever the reader points at. */
export const MAX_INTENT_REQUESTS = 4

/**
 * The pieces of a `ViewConfiguration` this menu exposes, flat and keyed on the identifiers the
 * section controls report. `sorts` collapses to a single field because Sort by is single-select.
 */
type MenuValues = {
  layout: Layout
  groupBy: string | null
  swimlaneBy: string | null
  sortField: string | null
  visibleFields: ReadonlySet<string>
}

function toMenuValues(configuration: ViewConfiguration): MenuValues {
  return {
    layout: configuration.layout,
    groupBy: configuration.groupBy,
    swimlaneBy: configuration.swimlaneBy,
    sortField: configuration.sorts[0]?.field ?? null,
    visibleFields: new Set(configuration.visibleFields),
  }
}

function visibleFieldsChanged(values: MenuValues, configuration: ViewConfiguration): boolean {
  return (
    values.visibleFields.size !== configuration.visibleFields.length ||
    configuration.visibleFields.some(identifier => !values.visibleFields.has(identifier))
  )
}

/** The settings `values` changes, in the shape `onApply` takes. */
function changedSettings(values: MenuValues, configuration: ViewConfiguration): Partial<ViewConfiguration> {
  const changes: Partial<ViewConfiguration> = {}
  if (values.layout !== configuration.layout) changes.layout = values.layout
  if (values.groupBy !== configuration.groupBy && values.groupBy !== null) changes.groupBy = values.groupBy
  if (values.swimlaneBy !== configuration.swimlaneBy) changes.swimlaneBy = values.swimlaneBy
  if (values.sortField !== (configuration.sorts[0]?.field ?? null)) {
    changes.sorts = values.sortField === null ? [] : [{field: values.sortField, direction: 'asc'}]
  }
  if (visibleFieldsChanged(values, configuration)) changes.visibleFields = [...values.visibleFields]
  return changes
}

const LOAD_ERROR_MESSAGE = 'Unable to load view options'

function LoadError() {
  return (
    <AriaAlert className={styles.loadError}>
      <AlertIcon className={styles.loadErrorIcon} />
      <span className={styles.loadErrorTitle}>{LOAD_ERROR_MESSAGE}</span>
      <span className={styles.loadErrorBody}>Try refreshing the page.</span>
    </AriaAlert>
  )
}

/**
 * Shown when the menu throws while rendering.
 */
function SavedViewOptionsErrorFallback() {
  return (
    <ViewOptionsMenu triggerLabel="View options" title="View options">
      <LoadError />
    </ViewOptionsMenu>
  )
}

/** Controlled saved-view options menu shared by domain-specific hosts. */
export function SavedViewOptionsMenu(props: SavedViewOptionsMenuProps) {
  return (
    <ErrorBoundary boundaryName="SavedViewOptionsMenu" fallback={<SavedViewOptionsErrorFallback />}>
      <SavedViewOptionsMenuInner {...props} />
    </ErrorBoundary>
  )
}

function SavedViewOptionsMenuInner({
  fields: view,
  isPending = false,
  isError = false,
  configuration,
  showDirtyIndicator = false,
  configurationDirty,
  onDiscard,
  disabled = false,
  onApply,
  onIntent,
}: SavedViewOptionsMenuProps) {
  const values = useMemo(() => toMenuValues(configuration), [configuration])

  const groupFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.GROUPABLE).map(toFieldOption) : []),
    [view],
  )
  const columnFields = useMemo(
    () => groupFields.map(field => ({...field, disabled: field.identifier === values.swimlaneBy})),
    [groupFields, values.swimlaneBy],
  )
  const swimlaneFields = useMemo(
    () => groupFields.map(field => ({...field, disabled: field.identifier === values.groupBy})),
    [groupFields, values.groupBy],
  )
  const attributeFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.ATTRIBUTABLE).map(toFieldOption) : []),
    [view],
  )
  const sortFields = useMemo(
    () => (view ? getFieldsForCapability(view, Capability.SORTABLE).map(toFieldOption) : []),
    [view],
  )

  const update = (edit: (current: MenuValues) => MenuValues) => {
    const changes = changedSettings(edit(values), configuration)
    if (Object.keys(changes).length > 0) onApply(changes)
  }

  const intentRequestsRef = useRef(0)
  const intendTo = (edit: (current: MenuValues) => MenuValues) => {
    if (!onIntent || intentRequestsRef.current >= MAX_INTENT_REQUESTS) return
    const changes = changedSettings(edit(values), configuration)
    if (Object.keys(changes).length > 0 && onIntent(changes)) intentRequestsRef.current += 1
  }
  const pickColumn = (current: MenuValues, next: string): MenuValues => ({
    ...current,
    groupBy: next,
    // If the picked field is also the current swimlane, clear the swimlane
    // so the two axes never collide, matching the previous direct mapping.
    swimlaneBy: current.swimlaneBy === next ? null : current.swimlaneBy,
  })

  const toggleVisible = (identifier: string, next: boolean) =>
    update(current => {
      const updated = new Set(current.visibleFields)
      if (next) updated.add(identifier)
      else updated.delete(identifier)
      return {...current, visibleFields: updated}
    })

  return (
    <ViewOptionsMenu
      triggerLabel="View options"
      title="View options"
      onOpenChange={open => {
        if (open) intentRequestsRef.current = 0
      }}
      showDirtyIndicator={showDirtyIndicator}
      onDiscard={onDiscard}
      disabled={disabled}
    >
      <ViewOptionsMenu.Layout
        label="Layout"
        dirtyValue={configurationDirty?.layout ? configuration.layout : undefined}
        trailing={values.layout === 'board' ? <Label variant="success">Alpha</Label> : undefined}
        options={LAYOUT_OPTIONS}
        value={values.layout}
        onSelect={next => update(current => ({...current, layout: next}))}
        onIntent={onIntent && (next => intendTo(current => ({...current, layout: next})))}
      />

      {values.layout === 'board' && (
        <ViewOptionsMenu.Group
          label="Configuration"
          status={
            isPending ? (
              <div className={styles.status}>
                <Spinner srText="Loading view options" />
              </div>
            ) : isError ? (
              <LoadError />
            ) : null
          }
        >
          {view && (
            <>
              <ViewOptionsMenu.MultiSelect
                icon={NoteIcon}
                label="Visible fields"
                showDirtyIndicator={configurationDirty?.visibleFields}
                placeholder="None"
                fields={attributeFields}
                selectedIds={values.visibleFields}
                onToggle={toggleVisible}
              />
              <ViewOptionsMenu.SingleSelect
                icon={ColumnsIcon}
                label="Column by"
                showDirtyIndicator={configurationDirty?.groupBy}
                placeholder="None"
                fields={columnFields}
                value={values.groupBy}
                onSelect={next => update(current => pickColumn(current, next))}
                onIntent={onIntent && (next => next !== null && intendTo(current => pickColumn(current, next)))}
              />
              <ViewOptionsMenu.SingleSelect
                icon={RowsIcon}
                label="Swimlane by"
                showDirtyIndicator={configurationDirty?.swimlaneBy}
                placeholder="None"
                fields={swimlaneFields}
                value={values.swimlaneBy}
                onSelect={next => update(current => ({...current, swimlaneBy: next}))}
                onClear={() => update(current => ({...current, swimlaneBy: null}))}
                onIntent={onIntent && (next => intendTo(current => ({...current, swimlaneBy: next})))}
              />
              <ViewOptionsMenu.SingleSelect
                icon={SortByIcon}
                label="Sort by"
                showDirtyIndicator={configurationDirty?.sorts}
                placeholder="None"
                fields={sortFields}
                value={values.sortField}
                onSelect={next => update(current => ({...current, sortField: next}))}
                onClear={() => update(current => ({...current, sortField: null}))}
                onIntent={onIntent && (next => intendTo(current => ({...current, sortField: next})))}
              />
            </>
          )}
        </ViewOptionsMenu.Group>
      )}
    </ViewOptionsMenu>
  )
}
